import { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../config";

// ─── helpers ────────────────────────────────────────────────────────────────
function formatEUR(n) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n || 0));
}
function startOfDayMs(ms) {
  const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime();
}
function toLocalInput(ms) {
  const d = new Date(ms);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fromLocalInput(val) { const t = Date.parse(val); return Number.isFinite(t) ? t : Date.now(); }
function relativeDate(ms) {
  const diff = Date.now() - ms;
  if (diff < 60000) return "ahora";
  if (diff < 3600000) return `hace ${Math.floor(diff/60000)}m`;
  if (diff < 86400000) return `hace ${Math.floor(diff/3600000)}h`;
  return new Date(ms).toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

// ─── sub-components ─────────────────────────────────────────────────────────
function KPICard({ icon, label, value, sub, color = "#e11d48" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px", display: "flex", gap: 14, alignItems: "center" }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function SplitBar({ ventaRev, subastaRev }) {
  const total = ventaRev + subastaRev || 1;
  const vPct = Math.round((ventaRev / total) * 100);
  const sPct = 100 - vPct;
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Venta vs Subasta</div>
      <div style={{ display: "flex", height: 10, borderRadius: 99, overflow: "hidden", gap: 2 }}>
        <div style={{ flex: vPct, background: "#e11d48", borderRadius: 99, transition: "flex .4s" }} />
        <div style={{ flex: sPct, background: "#f59e0b", borderRadius: 99, transition: "flex .4s" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12 }}>
        <span style={{ color: "#e11d48", fontWeight: 700 }}>🔴 Venta {vPct}% · {formatEUR(ventaRev)}</span>
        <span style={{ color: "#f59e0b", fontWeight: 700 }}>🟡 Subasta {sPct}% · {formatEUR(subastaRev)}</span>
      </div>
    </div>
  );
}

function MedalBadge({ pos }) {
  if (pos === 1) return <span title="1º">🥇</span>;
  if (pos === 2) return <span title="2º">🥈</span>;
  if (pos === 3) return <span title="3º">🥉</span>;
  return <span style={{ fontWeight: 700, opacity: 0.45, fontSize: 13 }}>{pos}</span>;
}

function StatusBadge({ status }) {
  const ok = status === "confirmed";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700, background: ok ? "#dcfce7" : "#fee2e2", color: ok ? "#15803d" : "#dc2626" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
      {ok ? "Confirmada" : "Anulada"}
    </span>
  );
}

function TypeBadge({ type }) {
  const isVenta = type === "venta";
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, background: isVenta ? "#fce7f3" : "#fef3c7", color: isVenta ? "#9d174d" : "#92400e" }}>
      {isVenta ? "Venta" : type === "subasta" ? "Subasta" : type}
    </span>
  );
}

function Toast({ toast, onClose }) {
  if (!toast) return null;
  const ok = toast.type === "success";
  return (
    <div style={{ position: "fixed", top: 60, right: 16, zIndex: 9999, padding: "12px 16px", borderRadius: 12, border: `1px solid ${ok ? "#86efac" : "#fca5a5"}`, background: ok ? "#f0fdf4" : "#fff1f2", color: "#111", boxShadow: "0 8px 30px rgba(0,0,0,0.12)", minWidth: 260, maxWidth: 380, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <span style={{ fontSize: 18 }}>{ok ? "✅" : "❌"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{ok ? "Listo" : "Error"}</div>
        <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{toast.message}</div>
      </div>
      <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", opacity: 0.5, fontSize: 16, lineHeight: 1 }}>×</button>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function Datos() {
  const [rangePreset, setRangePreset] = useState("7d");
  const [from, setFrom] = useState(() => startOfDayMs(Date.now() - 6 * 86400000));
  const [to,   setTo]   = useState(() => Date.now());
  const [status, setStatus] = useState("confirmed");
  const [q,      setQ]      = useState("");
  const [toast,  setToast]  = useState(null);

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState("");
  const [voiding, setVoiding] = useState({});
  const [offset,  setOffset]  = useState(0);
  const limit = 50;

  const [summary,    setSummary]    = useState(null);
  const [topUsers,   setTopUsers]   = useState([]);
  const [sortUsers,  setSortUsers]  = useState("revenue");
  const [statsLoading, setStatsLoading] = useState(false);

  // venta manual
  const [userQuery,    setUserQuery]    = useState("");
  const [userHits,     setUserHits]     = useState([]);
  const [userSearching,setUserSearching]= useState(false);
  const [mUserId,      setMUserId]      = useState("");
  const [mAmount,      setMAmount]      = useState("");
  const [mNote,        setMNote]        = useState("");
  const [creating,     setCreating]     = useState(false);
  const [showManual,   setShowManual]   = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3000);
  };

  // presets
  useEffect(() => {
    const now = Date.now();
    if (rangePreset === "today")    { setFrom(startOfDayMs(now)); setTo(now); }
    else if (rangePreset === "7d")  { setFrom(startOfDayMs(now - 6*86400000)); setTo(now); }
    else if (rangePreset === "30d") { setFrom(startOfDayMs(now - 29*86400000)); setTo(now); }
    setOffset(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangePreset]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ from: String(from), to: String(to), limit: String(limit), offset: String(offset) });
    if (status !== "all") p.set("status", status);
    if (q.trim()) p.set("q", q.trim());
    return p.toString();
  }, [from, to, status, q, offset]);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/transactions?${qs}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(await res.json());
    } catch (e) { setErr(String(e.message)); setRows([]); }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const base = new URLSearchParams({ from: String(from), to: String(to) }).toString();
      const usersQs = new URLSearchParams({ from: String(from), to: String(to), sort: sortUsers, limit: "20", offset: "0", q: q.trim() }).toString();
      const [r1, r2] = await Promise.all([
        fetch(`${API_BASE}/api/stats/summary?${base}`),
        fetch(`${API_BASE}/api/stats/users?${usersQs}`),
      ]);
      setSummary(await r1.json());
      setTopUsers(await r2.json().then(d => Array.isArray(d) ? d : []));
    } catch { setSummary(null); setTopUsers([]); }
    finally { setStatsLoading(false); }
  };

  useEffect(() => { load(); }, [qs]); // eslint-disable-line
  useEffect(() => { loadStats(); }, [from, to, sortUsers, q]); // eslint-disable-line

  // user search debounce
  useEffect(() => {
    if (!userQuery.trim()) { setUserHits([]); return; }
    let alive = true; setUserSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/search?${new URLSearchParams({ q: userQuery.trim(), limit: "8" })}`);
        const d = await res.json().catch(() => []);
        if (alive) setUserHits(Array.isArray(d) ? d : []);
      } finally { if (alive) setUserSearching(false); }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [userQuery]);

  const voidTx = async (id) => {
    if (!window.confirm("¿Anular esta compra?")) return;
    const reason = window.prompt("Motivo (opcional):", "") || "";
    setVoiding(p => ({ ...p, [id]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/transactions/${encodeURIComponent(id)}/void`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `HTTP ${res.status}`); }
      showToast("success", "Venta anulada");
      setRows(prev => prev.map(r => r.id === id ? { ...r, status: "voided", voidReason: reason || null } : r));
    } catch (e) { showToast("error", String(e.message)); }
    finally { setVoiding(p => ({ ...p, [id]: false })); }
  };

  const createManualSale = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/transactions/manual`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ liveUniqueId: "ventamanual", userId: mUserId.trim(), amount: Number(mAmount), note: mNote.trim() }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `HTTP ${res.status}`); }
      showToast("success", `Venta de ${formatEUR(mAmount)} registrada`);
      setMAmount(""); setMNote(""); setMUserId(""); setUserQuery(""); setShowManual(false);
      await load(); await loadStats();
    } catch (e) { showToast("error", String(e.message)); }
    finally { setCreating(false); }
  };

  const refreshNow = () => {
    const now = Date.now();
    if (rangePreset !== "custom") { setTo(now); if (rangePreset === "today") setFrom(startOfDayMs(now)); }
  };

  const canPrev = offset > 0;
  const canNext = rows.length === limit;

  const metrics = useMemo(() => {
    const confirmed = rows.filter(r => r.status === "confirmed");
    const totalRevenue = confirmed.reduce((a, r) => a + Number(r.amount||0), 0);
    return { confirmedCount: confirmed.length, voidedCount: rows.length - confirmed.length, totalRevenue, avgTicket: confirmed.length ? totalRevenue / confirmed.length : 0 };
  }, [rows]);

  const PRESETS = [
    { key: "today", label: "Hoy" },
    { key: "7d",    label: "7 días" },
    { key: "30d",   label: "30 días" },
    { key: "custom",label: "Personalizado" },
  ];
  const STATUS_OPTS = [
    { key: "confirmed", label: "Confirmadas" },
    { key: "voided",    label: "Anuladas" },
    { key: "all",       label: "Todas" },
  ];

  const pillStyle = (active) => ({
    border: active ? "none" : "1px solid var(--border)",
    borderRadius: 99, padding: "5px 14px", fontSize: 13, fontWeight: 700,
    cursor: "pointer", background: active ? "var(--brand-red)" : "#fff",
    color: active ? "#fff" : "var(--text)", transition: "all .15s",
  });

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 16px 40px" }}>
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Datos de ventas</h1>
          <div style={{ fontSize: 12, opacity: 0.5, marginTop: 2 }}>Historial · estadísticas · ranking</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={refreshNow} disabled={loading}>
            {loading ? "…" : "↻ Actualizar"}
          </button>
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => setShowManual(v => !v)}>
            {showManual ? "✕ Cancelar" : "+ Venta manual"}
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }}>
        <KPICard icon="💰" label="Facturación" value={summary ? formatEUR(summary.totalRevenue) : "—"} sub={`${summary?.totalTransactions ?? "—"} ventas`} color="#e11d48" />
        <KPICard icon="🎫" label="Ticket medio" value={summary ? formatEUR(summary.avgTicket) : "—"} color="#7c3aed" />
        <KPICard icon="👥" label="Compradores únicos" value={summary?.uniqueBuyers ?? topUsers.length ?? "—"} color="#0891b2" />
        <KPICard icon="📄" label="Esta página" value={metrics.confirmedCount} sub={`${metrics.voidedCount} anuladas · ${formatEUR(metrics.totalRevenue)}`} color="#059669" />
      </div>

      {/* ── Split bar ── */}
      {summary && (
        <div style={{ marginBottom: 16 }}>
          <SplitBar ventaRev={summary.byType?.venta?.revenue || 0} subastaRev={summary.byType?.subasta?.revenue || 0} />
        </div>
      )}

      {/* ── Filtros ── */}
      <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 14, padding: "14px 16px", marginBottom: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {/* Rango */}
          <div style={{ display: "flex", gap: 4 }}>
            {PRESETS.map(p => (
              <button key={p.key} style={pillStyle(rangePreset === p.key)} onClick={() => setRangePreset(p.key)}>{p.label}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 24, background: "#e5e7eb", margin: "0 4px" }} />
          {/* Estado */}
          <div style={{ display: "flex", gap: 4 }}>
            {STATUS_OPTS.map(s => (
              <button key={s.key} style={pillStyle(status === s.key)} onClick={() => { setStatus(s.key); setOffset(0); }}>{s.label}</button>
            ))}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <input
              value={q} onChange={e => { setQ(e.target.value); setOffset(0); }}
              placeholder="🔍 Buscar nombre, @, id…"
              style={{ padding: "7px 12px", border: "1px solid var(--border)", borderRadius: 99, fontSize: 13, width: 220, outline: "none" }}
            />
            {q && <button style={{ border: "none", background: "none", cursor: "pointer", opacity: 0.5, fontSize: 18 }} onClick={() => { setQ(""); setOffset(0); }}>×</button>}
          </div>
        </div>

        {rangePreset === "custom" && (
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid var(--border)", paddingTop: 10 }}>
            <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
              Desde <input type="datetime-local" value={toLocalInput(from)} onChange={e => { setFrom(fromLocalInput(e.target.value)); setOffset(0); }} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "4px 8px", fontSize: 13 }} />
            </label>
            <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
              Hasta <input type="datetime-local" value={toLocalInput(to)} onChange={e => { setTo(fromLocalInput(e.target.value)); setOffset(0); }} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "4px 8px", fontSize: 13 }} />
            </label>
          </div>
        )}
      </div>

      {/* ── Venta manual (collapsible) ── */}
      {showManual && (
        <div style={{ background: "#fff", border: "2px solid var(--brand-red)", borderRadius: 14, padding: "16px 20px", marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>Nueva venta manual</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            {/* User search */}
            <div style={{ position: "relative", flex: "1 1 240px" }}>
              <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.6, display: "block", marginBottom: 4 }}>Cliente</label>
              <input value={userQuery} onChange={e => setUserQuery(e.target.value)}
                placeholder="Buscar @usuario o nombre…"
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }} />
              {userQuery.trim() && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "#fff", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", overflow: "hidden", marginTop: 4 }}>
                  {userSearching ? (
                    <div style={{ padding: 10, fontSize: 13, opacity: 0.6 }}>Buscando…</div>
                  ) : userHits.length === 0 ? (
                    <div style={{ padding: 10, fontSize: 13, opacity: 0.6 }}>Sin resultados</div>
                  ) : userHits.map(u => (
                    <button key={u.id} onClick={() => { setMUserId(String(u.id)); setUserQuery(`${u.displayName || u.lastKnownNickname || u.id} (@${u.lastKnownUniqueId || "—"})`); setUserHits([]); }}
                      style={{ width: "100%", textAlign: "left", padding: "10px 12px", border: "none", background: "#fff", cursor: "pointer", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                      <strong>{u.displayName || u.lastKnownNickname || "—"}</strong>
                      <span style={{ opacity: 0.6 }}> @{u.lastKnownUniqueId}</span>
                    </button>
                  ))}
                </div>
              )}
              {mUserId && <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>id: {mUserId}</div>}
            </div>
            {/* Importe */}
            <div style={{ flex: "0 0 120px" }}>
              <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.6, display: "block", marginBottom: 4 }}>Importe (€)</label>
              <input value={mAmount} onChange={e => setMAmount(e.target.value)} placeholder="0.00" inputMode="decimal"
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            {/* Nota */}
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.6, display: "block", marginBottom: 4 }}>Nota (opcional)</label>
              <input value={mNote} onChange={e => setMNote(e.target.value)} placeholder="Artículo, motivo…"
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <button className="btn btn-primary" onClick={createManualSale} disabled={creating || !mUserId || !mAmount}
                style={{ padding: "10px 20px", fontSize: 14, borderRadius: 10 }}>
                {creating ? "Creando…" : "Registrar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, alignItems: "flex-start" }}>

        {/* ── Tabla ── */}
        <div>
          {err && <div style={{ padding: "10px 14px", background: "#fff1f2", border: "1px solid #fca5a5", borderRadius: 10, color: "#dc2626", marginBottom: 12, fontSize: 13 }}>⚠ {err}</div>}

          <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
            {/* table header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 80px 100px 80px", padding: "10px 16px", background: "#f8f9fa", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 700, opacity: 0.55, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span>Cliente</span><span>Tipo</span><span style={{ textAlign: "right" }}>Importe</span><span>Estado</span><span>Acción</span>
            </div>

            {loading && <div style={{ padding: 20, textAlign: "center", opacity: 0.5, fontSize: 13 }}>Cargando…</div>}

            {!loading && rows.length === 0 && (
              <div style={{ padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                <div style={{ opacity: 0.5, fontSize: 14 }}>No hay ventas para estos filtros</div>
              </div>
            )}

            {rows.map((r, i) => (
              <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1fr 90px 80px 100px 80px", padding: "12px 16px", borderBottom: i < rows.length-1 ? "1px solid #f3f4f6" : "none", alignItems: "center", background: r.status === "voided" ? "#fafafa" : "#fff", opacity: r.status === "voided" ? 0.7 : 1 }}>
                {/* cliente */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.displayName || r.lastKnownNickname || "—"}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.55 }}>@{r.lastKnownUniqueId || "—"} · {relativeDate(r.createdAt)}</div>
                  {r.note && <div style={{ fontSize: 11, opacity: 0.45, fontStyle: "italic" }}>{r.note}</div>}
                  {r.status === "voided" && r.voidReason && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>↩ {r.voidReason}</div>}
                </div>
                {/* tipo */}
                <div><TypeBadge type={r.type} /></div>
                {/* importe */}
                <div style={{ textAlign: "right", fontWeight: 800, fontSize: 14, color: r.status === "voided" ? "#9ca3af" : "#111", textDecoration: r.status === "voided" ? "line-through" : "none" }}>
                  {formatEUR(r.amount)}
                </div>
                {/* estado */}
                <div><StatusBadge status={r.status} /></div>
                {/* acción */}
                <div>
                  {r.status === "confirmed" ? (
                    <button onClick={() => voidTx(r.id)} disabled={voiding[r.id]}
                      style={{ border: "1px solid #fca5a5", borderRadius: 8, padding: "4px 10px", fontSize: 12, background: "#fff", color: "#dc2626", cursor: "pointer", fontWeight: 600 }}>
                      {voiding[r.id] ? "…" : "Anular"}
                    </button>
                  ) : <span style={{ opacity: 0.3, fontSize: 12 }}>—</span>}
                </div>
              </div>
            ))}
          </div>

          {/* paginación */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
            <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={() => setOffset(o => Math.max(0, o-limit))} disabled={!canPrev || loading}>← Anterior</button>
            <span style={{ fontSize: 12, opacity: 0.5 }}>{rows.length} registros · página {Math.floor(offset/limit)+1}</span>
            <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={() => setOffset(o => o+limit)} disabled={!canNext || loading}>Siguiente →</button>
          </div>
        </div>

        {/* ── Sidebar: Top compradores ── */}
        <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", position: "sticky", top: 60 }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>Top compradores</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[["revenue","€"], ["count","#"]].map(([k, label]) => (
                <button key={k} onClick={() => setSortUsers(k)} style={{ border: "none", borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: sortUsers === k ? "var(--brand-red)" : "#f3f4f6", color: sortUsers === k ? "#fff" : "var(--text)" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {statsLoading && <div style={{ padding: 20, textAlign: "center", opacity: 0.5, fontSize: 13 }}>Cargando…</div>}

          {!statsLoading && topUsers.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", opacity: 0.45, fontSize: 13 }}>Sin datos</div>
          )}

          {topUsers.map((u, idx) => (
            <div key={u.userId} style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 16px", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ width: 28, textAlign: "center", flexShrink: 0, fontSize: 18 }}><MedalBadge pos={idx+1} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.displayName || u.lastKnownNickname || "—"}
                </div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>@{u.lastKnownUniqueId || "—"}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "var(--brand-red)" }}>{formatEUR(u.revenue)}</div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>{u.count} compra{u.count !== 1 ? "s" : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
